import { toNumber } from "@/features/account/api/mappers";
import type {
  ApplicantsResult,
  ApplicantTask,
  ApplyVerification,
  BucketsResult,
  DeviceEarner,
  JobBucket,
  FinishedTask,
  FinishedTaskDetail,
  FinishedTasksResult,
  JobDetail,
  JobEmployer,
  JobInvitation,
  PostedOffer,
  JobPermission,
  JobSearchResult,
  JobSharing,
  JobStats,
  JobStatus,
  JobSummary,
  JobType,
  SharedUser,
  MyJob,
  MyJobsResult,
  OtherJob,
  PaymentType,
  PlannedTask,
  PlannedTasksResult,
  PlatformStats,
  ReferralEarner,
  SharedJob,
  SharedJobsResult,
  StatsDevice,
  StatsJob,
  SuccessRateCell,
} from "@/features/jobs/types";

import type {
  ApplicantsResponseDto,
  ApplicantTaskDto,
  ApplyVerifyDto,
  BucketDto,
  BucketsResponseDto,
  DeviceEarnerDto,
  FinishedTaskDetailDto,
  FinishedTaskDto,
  FinishedTasksDto,
  InvitationsDto,
  MyOffersDto,
  PostedOfferDto,
  JobDetailDto,
  JobEmployerDto,
  JobInvitationDto,
  JobSearchResponseDto,
  JobSearchRowDto,
  MyJobRowDto,
  MyJobsResponseDto,
  OtherJobDto,
  PlannedTaskDto,
  PlannedTasksResponseDto,
  PlatformStatsDto,
  ReferralEarnerDto,
  SharedJobRowDto,
  SharedJobsResponseDto,
  SharedUserDto,
  SharingResponseDto,
  StatsDeviceDto,
  StatsJobDto,
  SuccessRateCellDto,
} from "./dto";

const count = (value: unknown) => Math.max(0, Math.trunc(toNumber(value, 0)));

function jobType(value?: string): JobType {
  return value === "devices_automation" ? "devices_automation" : "job";
}

function paymentType(value?: string): PaymentType | null {
  if (value === "action" || value === "screenshot") return value;
  return null;
}

/** Resolve an uploaded avatar to the app's `/uploads` path (Phase 8 redirect). */
function avatarUrl(avatar?: string | null): string | null {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  return `/uploads/profiles/${avatar}`;
}

/**
 * Reward shown to the worker. For non-automation action jobs run on a rented
 * device, the platform deducts the device-owner share — matching the legacy
 * `job_price - job_price * device_owner_share` computation.
 */
function netPrice(
  type: JobType,
  payment: PaymentType | null,
  automationId: string | null,
  jobPrice: number,
  deviceOwnerShare: number,
): number {
  if (type === "devices_automation" && payment === "action" && !automationId) {
    return jobPrice - jobPrice * deviceOwnerShare;
  }
  return jobPrice;
}

export function mapJobSummary(
  dto: JobSearchRowDto,
  deviceOwnerShare: number,
): JobSummary {
  const type = jobType(dto.job_type);
  const payment = paymentType(dto.payment_type);
  const automationId = dto.automation_id ?? null;
  const targetWorkers = dto.Target_Workers ?? [];
  return {
    jobId: String(dto.job_id ?? ""),
    title: dto.title ?? "Untitled job",
    jobType: type,
    paymentType: payment,
    automationId,
    premium: Boolean(dto.premium),
    featured: Boolean(dto.featured),
    worldwide: targetWorkers.includes("worldwide"),
    targetWorkers,
    price: netPrice(
      type,
      payment,
      automationId,
      toNumber(dto.job_price, 0),
      deviceOwnerShare,
    ),
    duration: count(dto.duration),
    positions: count(dto.positions),
    jobDone: count(dto.job_done),
    totalTasks: count(dto.total_task),
    pendingTasks: count(dto.pending_tasks),
    satisfiedTasks: count(dto.satisfied_tasks),
    added: dto.added ?? null,
  };
}

export function mapJobSearch(dto: JobSearchResponseDto): JobSearchResult {
  const deviceOwnerShare = toNumber(dto.device_owner_share, 0);
  return {
    jobs: (dto.Jobs ?? []).map((row) => mapJobSummary(row, deviceOwnerShare)),
    totalCount: count(dto.totalCount),
    totalPages: count(dto.totalPages),
    currentPage: Math.max(1, count(dto.currentPage)),
    deviceOwnerShare,
  };
}

function mapEmployer(dto?: JobEmployerDto | null): JobEmployer | null {
  if (!dto) return null;
  return {
    userId: String(dto.user_id ?? ""),
    username: dto.username ?? "",
    avatarUrl: avatarUrl(dto.avatar),
    countryId: dto.country_id ?? "",
    createdAt: dto.created ?? null,
    lastActivity: dto.last_activity ?? null,
    totalJobs: count(dto.total_job),
    totalSpend: toNumber(dto.total_spend, 0),
    rating: Math.min(5, Math.max(0, toNumber(dto.total_tasks, 0))),
  };
}

function mapOtherJob(dto: OtherJobDto): OtherJob {
  return {
    jobId: String(dto.job_id ?? ""),
    title: dto.title ?? "Untitled job",
    jobType: jobType(dto.job_type),
  };
}

export function mapJobDetail(dto: JobDetailDto): JobDetail {
  const type = jobType(dto.job_type);
  const payment = paymentType(dto.payment_type);
  const automationId = dto.automation_id ?? null;
  const targetWorkers = dto.Target_Workers ?? [];
  return {
    jobId: String(dto.job_id ?? ""),
    title: dto.title ?? "Untitled job",
    jobType: type,
    paymentType: payment,
    automationId,
    category: dto.category ?? "",
    description: dto.description ?? "",
    proof: dto.proof ?? "",
    premium: Boolean(dto.premium),
    featured: Boolean(dto.featured),
    worldwide: targetWorkers.includes("worldwide"),
    targetWorkers,
    price: netPrice(
      type,
      payment,
      automationId,
      toNumber(dto.job_price, 0),
      toNumber(dto.device_owner_share, 0),
    ),
    duration: count(dto.duration),
    positions: count(dto.positions),
    tasksDoneCount: count(dto.tasks_done_count),
    totalTasksCount: count(dto.totla_tasks_count),
    tasksPendingCount: count(dto.tasks_pending_count),
    tasksConfirmedCount: count(dto.tasks_confirmed_count),
    cachedBeforeSeconds:
      typeof dto.cached_before_seconds === "number"
        ? dto.cached_before_seconds
        : null,
    added: dto.added ?? null,
    status: (dto.status as JobDetail["status"]) ?? "active",
    jobStatusOpen: Boolean(dto.job_status),
    isMyJob: Boolean(dto.is_myjob),
    isPremiumBlocked: Boolean(dto.is_premimum),
    isJobCompleted: Boolean(dto.is_jobcompleted),
    isAvailableCountry: Boolean(dto.is_avialblecountry),
    fewTimes: Boolean(dto.few_times),
    employer: mapEmployer(dto.user),
    otherJobs: (dto.other_job ?? []).map(mapOtherJob),
  };
}

export function mapApplyVerification(dto: ApplyVerifyDto): ApplyVerification {
  const job = dto.verify_job ?? {};
  const user = dto.verify_user ?? {};
  return {
    jobId: String(job.job_id ?? ""),
    title: job.title ?? "Untitled job",
    description: job.description ?? "",
    proof: job.proof ?? "",
    duration: count(job.duration),
    premium: Boolean(job.premium),
    isProofFile: Boolean(job.is_proof_file),
    customVars: job.custom_vars ?? null,
    inputFields: job.input_fields ?? [],
    files: (job.files ?? []).map((f) => ({
      fieldName: f.field_name ?? "",
      type: f.type ?? null,
      limitMb: toNumber(f.limit_mb, 0),
      required: Boolean(f.required),
    })),
    user: {
      countryId: user.country_id ?? "",
      rate: toNumber(user.rate, 0),
      premium: Boolean(user.premium),
      canApplyAgain: Boolean(user.CanApply_again),
    },
  };
}

function mapStatsJob(dto: StatsJobDto): StatsJob {
  return {
    jobId: String(dto.job_id ?? ""),
    title: dto.title ?? "Untitled job",
    automationId: dto.automation_id ?? null,
  };
}

function mapStatsDevice(dto: StatsDeviceDto): StatsDevice {
  return {
    remoteDeviceId: String(dto.remote_device_id ?? ""),
    name: dto.name ?? "",
    brand: dto.brand ?? "",
    model: dto.model ?? "",
    ownerId: String(dto.owner?.user_id ?? ""),
    ownerUsername: dto.owner?.username ?? "",
  };
}

function mapCell(dto: SuccessRateCellDto): SuccessRateCell {
  return {
    successfulTasks: count(dto.successful_tasks),
    failedTasks: count(dto.failed_tasks),
    totalTasks: count(dto.total_tasks),
    successRate: Math.min(100, Math.max(0, toNumber(dto.success_rate, 0))),
  };
}

function mapDeviceEarner(dto: DeviceEarnerDto): DeviceEarner {
  return {
    deviceId: String(dto.device_id ?? ""),
    deviceName: dto.device_name ?? "",
    brand: dto.device_brand ?? "",
    model: dto.device_model ?? "",
    ownerUsername: dto.owner_username ?? "",
    totalEarnings: toNumber(dto.totalEarnings, 0),
    taskCount: count(dto.taskCount),
  };
}

function mapReferralEarner(dto: ReferralEarnerDto): ReferralEarner {
  return {
    username: dto.username ?? "",
    totalEarnings: toNumber(dto.totalEarnings, 0),
  };
}

export function mapPlatformStats(dto: PlatformStatsDto): PlatformStats {
  const matrix: PlatformStats["matrix"] = {};
  for (const [jobId, devices] of Object.entries(dto.matrix ?? {})) {
    matrix[jobId] = {};
    for (const [deviceId, cell] of Object.entries(devices ?? {})) {
      matrix[jobId][deviceId] = mapCell(cell);
    }
  }
  return {
    jobs: (dto.jobs ?? []).map(mapStatsJob),
    devices: (dto.devices ?? []).map(mapStatsDevice),
    matrix,
    topReferralEarners: (dto.topReferralEarners ?? []).map(mapReferralEarner),
    topDeviceEarners: (dto.topDeviceEarners ?? []).map(mapDeviceEarner),
  };
}

export function mapInvitation(dto: JobInvitationDto): JobInvitation {
  return {
    inviteId: String(dto.invite_id ?? ""),
    invited: dto.invited ?? null,
    jobId: String(dto.job_id ?? ""),
    jobTitle: dto.job ?? "Untitled job",
    employer: dto.employer ?? "",
    employerId: String(dto.employer_id ?? ""),
    price: toNumber(dto.job_price, 0),
    tasksDone: count(dto.taskdone),
    positions: count(dto.positions),
    comment: dto.comment ?? null,
  };
}

export function mapInvitations(dto: InvitationsDto): JobInvitation[] {
  return (dto.invitations ?? []).map(mapInvitation);
}

export function mapOffer(dto: PostedOfferDto): PostedOffer {
  return {
    offerId: String(dto.job_id ?? ""),
    title: dto.title ?? "Untitled offer",
    reward: toNumber(dto.reward, 0),
    applicants: count(dto.applicants),
    positions: count(dto.positions),
    posted: dto.posted ?? null,
    status: dto.status ?? "active",
  };
}

export function mapOffers(dto: MyOffersDto): PostedOffer[] {
  return (dto.offers ?? []).map(mapOffer);
}

export function mapFinishedTask(dto: FinishedTaskDto): FinishedTask {
  return {
    taskId: String(dto.job_task_id ?? ""),
    jobId: String(dto.job_id ?? ""),
    jobTitle: dto.job_title ?? "Untitled job",
    price: toNumber(dto.job_price, 0),
    status: dto.status ?? "",
    added: dto.added ?? null,
    comment: dto.comment ?? null,
    failureReason: dto.failureReason ?? null,
  };
}

export function mapFinishedTasks(dto: FinishedTasksDto): FinishedTasksResult {
  return {
    totalTasks: count(dto.total_tasks),
    pendingCount: count(dto.jobspending_count),
    paidCount: count(dto.jobspaid_count),
    declinedCount: count(dto.jobsdecliend_count),
    failedCount: count(dto.jobsfailed_count),
    tasks: (dto.data ?? []).map(mapFinishedTask),
  };
}

export function mapFinishedTaskDetail(
  dto: FinishedTaskDetailDto,
): FinishedTaskDetail {
  return {
    ...mapFinishedTask(dto),
    updated: dto.updated ?? null,
    jobProof: dto.job_proof ?? "",
    proofFile: dto.proof_file ?? null,
    proofFiles: dto.proof_files ?? [],
    screen: dto.screen ?? null,
  };
}

const MY_JOB_STATUSES: JobStatus[] = [
  "active",
  "pending",
  "inactive",
  "declined",
  "complete",
];

export function mapMyJob(dto: MyJobRowDto): MyJob {
  const status = MY_JOB_STATUSES.includes(dto.status as JobStatus)
    ? (dto.status as JobStatus)
    : "pending";
  return {
    jobId: String(dto.job_id ?? ""),
    title: dto.title ?? "Untitled job",
    jobType: jobType(dto.job_type),
    automationId: dto.automation_id ?? null,
    premium: Boolean(dto.premium),
    featured: Boolean(dto.featured),
    // The owner sees the gross price they set (no device-owner-share deduction).
    price: toNumber(dto.job_price, 0),
    positions: count(dto.positions),
    jobDone: count(dto.job_done),
    pendingTasks: count(dto.pending_task ?? dto.pending_tasks),
    status,
    added: dto.added ?? null,
    reason: dto.reason ?? null,
  };
}

export function mapMyJobs(dto: MyJobsResponseDto): MyJobsResult {
  return {
    count: count(dto.count),
    page: Math.max(1, count(dto.page)),
    jobs: (dto.data ?? []).map(mapMyJob),
  };
}

/** Map one "Shared With Me" job; `permission` normalizes to view unless the
 * owner granted edit access. */
export function mapSharedJob(dto: SharedJobRowDto): SharedJob {
  const status = MY_JOB_STATUSES.includes(dto.status as JobStatus)
    ? (dto.status as JobStatus)
    : "pending";
  return {
    jobId: String(dto.job_id ?? ""),
    title: dto.title ?? "Untitled job",
    jobType: jobType(dto.job_type),
    price: toNumber(dto.job_price, 0),
    positions: count(dto.positions),
    jobDone: count(dto.job_done),
    status,
    added: dto.added ?? null,
    permission: dto.permission === "edit" ? "edit" : "view",
    ownerId: String(dto.user_id ?? ""),
    ownerUsername: dto.owner_username ?? "unknown",
  };
}

/** Map the "Shared With Me" list (independent pagination via `shared_page`). */
export function mapSharedJobs(dto: SharedJobsResponseDto): SharedJobsResult {
  return {
    count: count(dto.shared_count),
    jobs: (dto.shared_data ?? []).map(mapSharedJob),
  };
}

export function mapApplicantTask(dto: ApplicantTaskDto): ApplicantTask {
  const fullName = [dto.fname, dto.lname]
    .filter((p) => p && p.trim())
    .join(" ")
    .trim();
  // Device info is present only for device-automation submissions.
  const device =
    dto.device_owner || dto.device_name || dto.device_country
      ? {
          owner: dto.device_owner ?? "",
          ownerId: String(dto.iteration_device_owner_id ?? ""),
          name: dto.device_name ?? "",
          country: dto.device_country ?? "",
        }
      : null;
  return {
    taskId: String(dto.job_task_id ?? ""),
    jobId: String(dto.job_id ?? ""),
    workerId: String(dto.worker_id ?? ""),
    workerName: dto.worker_name ?? "Unknown worker",
    workerFullName: fullName || null,
    // The backend stores a comma-separated list; show the first address only.
    workerIp: dto.worker_ip ? (dto.worker_ip.split(",")[0] ?? null) : null,
    status: dto.status ?? "",
    added: dto.added ?? null,
    jobProof: dto.job_proof ?? "",
    proofInput: dto.proof_input ?? null,
    proofFile: dto.proof_file ?? null,
    proofFiles: dto.proof_files ?? [],
    screen: dto.screen ?? null,
    comment: dto.comment ?? null,
    failureReason: dto.failureReason ?? null,
    device,
  };
}

/** Normalize the backend permission string; anything unrecognized (including
 * a missing field, which the legacy app treated as `owner`) falls back to
 * `owner` so a stale/short response never silently locks the owner out. */
function jobPermission(value: string | undefined): JobPermission {
  return value === "edit" || value === "view" ? value : "owner";
}

/** Map the job summary stats shown in the applicants header. Country targets
 * come from the automation device list or the regular job's target workers. */
function mapJobStats(dto: ApplicantsResponseDto): JobStats {
  const countries = dto.automation_id
    ? dto.allowed_device_countries
    : dto.Target_Workers;
  return {
    duration: count(dto.duration),
    price: toNumber(dto.job_price, 0),
    positions: count(dto.positions),
    jobDone: count(dto.job_done),
    totalTasks: count(dto.total_task),
    pendingTasks: count(dto.pending_tasks),
    satisfiedTasks: count(dto.satisfied_tasks),
    declinedTasks: count(dto.declined_tasks),
    failedTasks: count(dto.failed_tasks),
    allowedWorkersCount: Array.isArray(dto.allowed_workers)
      ? dto.allowed_workers.length
      : 0,
    countries: Array.isArray(countries) ? countries : [],
  };
}

export function mapApplicants(dto: ApplicantsResponseDto): ApplicantsResult {
  // A search that matches nothing comes back as `job_tasks: [{}]` — one empty
  // object, not an empty array (verified against the live route 2026-08-14).
  // Mapped straight through it became a submission row reading "Unknown worker
  // / — / No proof submitted", a task that does not exist. Anything without an
  // id isn't a submission, so it never reaches the table or the count.
  const taskDtos = (dto.job_tasks ?? []).filter(
    (t) => t.job_task_id != null || t.worker_id != null,
  );

  return {
    jobId: "",
    // The live route returns the title at the top level; `Jobs` is never in the
    // payload, so this always fell through — and the old fallback was the PAGE'S
    // name, which made "JOB TITLE: Job applicants" read like real job data.
    // "Untitled job" matches how the rest of this file reports a missing title.
    jobTitle: dto.title ?? dto.Jobs?.title ?? "Untitled job",
    jobType: jobType(dto.job_type),
    paymentType: paymentType(dto.payment_type),
    isProofFile: Boolean(dto.is_proof_file),
    permission: jobPermission(dto.permission),
    automationId: dto.automation_id ?? null,
    stats: mapJobStats(dto),
    tasks: taskDtos.map(mapApplicantTask),
    // The live route omits `totalCount` entirely (verified 2026-08-14), so
    // `count()` produced 0 and the readout rendered "Showing 0–0 of 0" above a
    // full page of submissions. With no server-side total, the rows actually
    // returned are the only honest count.
    totalCount: count(dto.totalCount) || taskDtos.length,
    totalPages: Math.max(1, count(dto.totalPages)),
    currentPage: Math.max(1, count(dto.currentPage)),
  };
}

/** Map a single shared user; drops entries missing an id so downstream
 * keys/profile links are always well-formed. */
export function mapSharedUser(dto: SharedUserDto): SharedUser {
  return { userId: String(dto.user_id ?? ""), username: dto.username ?? "" };
}

/** Map the sharing response into the two grouped collaborator lists. */
export function mapSharing(dto: SharingResponseDto): JobSharing {
  const group = (users: SharedUserDto[] | undefined): SharedUser[] =>
    (users ?? []).filter((u) => u.user_id != null).map(mapSharedUser);
  return {
    view: group(dto.sharing?.view),
    edit: group(dto.sharing?.edit),
  };
}

/** Map one device bucket; a null/absent `data` object becomes `{}`. */
export function mapBucket(dto: BucketDto): JobBucket {
  return {
    bucketId: String(dto.bucket_id ?? ""),
    remoteDeviceId: dto.remote_device_id ?? "",
    deviceName: dto.device_name ?? null,
    data: dto.data ?? {},
    updatedAt: dto.updated_at ?? null,
  };
}

/** Map a page of device buckets for an automation job. */
export function mapBuckets(dto: BucketsResponseDto): BucketsResult {
  return {
    buckets: (dto.buckets ?? []).map(mapBucket),
    totalCount: count(dto.total),
    totalPages: Math.max(1, count(dto.pages)),
  };
}

/** Parse a planned task's JSON `input` into an object; non-JSON becomes `{}`. */
function parsePlannedInput(input: string | undefined): Record<string, unknown> {
  if (!input) return {};
  try {
    const parsed = JSON.parse(input);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/** Map one planned task; `job_task_id` is null until the task is dispatched. */
export function mapPlannedTask(dto: PlannedTaskDto): PlannedTask {
  return {
    plannedTaskId: String(dto.planned_task_id ?? ""),
    jobTaskId: dto.job_task_id != null ? String(dto.job_task_id) : null,
    input: parsePlannedInput(dto.input),
    added: dto.added ?? null,
  };
}

/** Map the planned-tasks envelope into a page of tasks + total count. */
export function mapPlannedTasks(dto: PlannedTasksResponseDto): PlannedTasksResult {
  return {
    tasks: (dto.data?.plannedTasks ?? []).map(mapPlannedTask),
    totalCount: count(dto.data?.total),
  };
}
